import { useState } from 'react'
import { apiClient } from '../services/api'
import UserList from './UserList'
import UserForm from './UserForm'
import UserPermissionManager from './UserPermissionManager'
import type { UserDetailResponse } from '../types/api'

type ViewMode = 'list' | 'create' | 'edit' | 'permissions'

export default function UserManagement() {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [selectedUser, setSelectedUser] = useState<UserDetailResponse | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleCreateClick = () => {
    setSelectedUser(null)
    setViewMode('create')
  }

  const handleEditClick = (user: UserDetailResponse) => {
    setSelectedUser(user)
    setViewMode('edit')
  }

  const handleDeleteClick = async (userId: number) => {
    try {
      await apiClient.deleteUser(userId)
      setSuccessMessage('ユーザーを削除しました')
      setRefreshTrigger((prev) => prev + 1)
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      console.error('Failed to delete user:', err)
      alert('ユーザーの削除に失敗しました')
    }
  }

  const handleFormSubmit = (success: boolean) => {
    if (success) {
      setSuccessMessage(viewMode === 'create' ? 'ユーザーを作成しました' : 'ユーザーを更新しました')
      setRefreshTrigger((prev) => prev + 1)
      setTimeout(() => {
        setViewMode('list')
        setSuccessMessage(null)
      }, 1500)
    }
  }

  const handleFormCancel = () => {
    setViewMode('list')
    setSelectedUser(null)
  }

  const handlePermissionsClick = (user: UserDetailResponse) => {
    setSelectedUser(user)
    setViewMode('permissions')
  }

  const handlePermissionsClose = () => {
    setViewMode('list')
    setSelectedUser(null)
    setRefreshTrigger((prev) => prev + 1)
  }

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h3 className="mb-0">
            <i className="fas fa-users me-2" />
            ユーザー管理
          </h3>
        </div>
        {viewMode === 'list' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleCreateClick}>
              <i className="fas fa-plus me-2" />
              新規ユーザー作成
            </button>
          </div>
        )}
      </div>

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
          <i className="fas fa-check-circle me-2" />
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage(null)}
          />
        </div>
      )}

      <div className="row">
        <div className="col-12">
          {viewMode === 'list' ? (
            <UserList
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onManagePermissions={handlePermissionsClick}
              refreshTrigger={refreshTrigger}
            />
          ) : viewMode === 'create' || viewMode === 'edit' ? (
            <UserForm
              user={selectedUser}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          ) : viewMode === 'permissions' && selectedUser ? (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <UserPermissionManager
                    userId={selectedUser.id}
                    userName={selectedUser.username}
                    onClose={handlePermissionsClose}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Floating Action Button for Permissions (when in list view) */}
      {viewMode === 'list' && (
        <div className="position-fixed" style={{ bottom: '2rem', right: '2rem', zIndex: 1000 }}>
          <div
            className="alert alert-info mb-2 p-2"
            style={{ minWidth: '200px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          >
            <small className="d-block mb-2">
              <i className="fas fa-lightbulb me-1" />
              ユーザー行の
              <strong>編集ボタン</strong>で権限を管理できます。
            </small>
          </div>
        </div>
      )}
    </div>
  )
}
